import { DateFnsLocaleProvider } from '@if/core/date'
import { GlobalProvider } from '@if/mui'
import { Container, CssBaseline } from '@mui/material'
import { useIsAppLoading, useIsDeprecated } from '@src/data/selectors/app-config-selectors'
import { useIsIcebergGrDevs } from '@src/providers/UserProvider'
import { useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter as Router } from 'react-router-dom'
import AlertBanner from './AlertBanner'
import AppHeader from './AppHeader'
import ConflictController from './components/ConflictController'
import LocaleProvider from './components/LocaleProvider'
import ThemeProvider from './components/ThemeProvider'
import TopLevelRoutes from './Routes'

const App = (): JSX.Element | null => {
  const [isLoaded, setLoaded] = useState(false)
  const isIcebergGrDevs = useIsIcebergGrDevs()
  const deprecated = useIsDeprecated()
  const { t } = useTranslation()

  const hasAlertBanner = isIcebergGrDevs || (import.meta.env.PROD && deprecated)

  const isLoadingConfig = useIsAppLoading()

  useEffect(() => {
    if (!isLoadingConfig) {
      setLoaded(true)
    }
  }, [isLoadingConfig])

  if (!isLoaded) return null

  return (
    <ThemeProvider>
      <>
        <CssBaseline />
        <GlobalProvider>
          <DateFnsLocaleProvider>
            <LocaleProvider>
              <Router>
                <AlertBanner message="Production" show={isIcebergGrDevs} />
                <AlertBanner
                  message={t('common.deprecated')}
                  variant="error"
                  show={import.meta.env.PROD && deprecated}
                />
                <AppHeader hasAlertBanner={hasAlertBanner} />
                <Container maxWidth="xl" disableGutters>
                  <TopLevelRoutes />
                </Container>
                <ConflictController />
              </Router>
            </LocaleProvider>
          </DateFnsLocaleProvider>
        </GlobalProvider>
      </>
    </ThemeProvider>
  )
}

export default App
